import { MouseEvent, useState } from 'react';
import MedList from './MedList.tsx';
import TradingPartnerSelect from './TradingPartnerSelect.tsx';
import mutationHooks from '../../common/mutationHooks/index.ts';
import CustomerLocationSelect from './CustomerLocationSelect.tsx';
import AdditionalData from './AdditionalData.tsx';
import { MedAPI } from '../../common/api/med.ts';
import { VerifiedTradingPartnerAPI } from '../../common/api/verifiedTradingPartner.ts';
import FinalReviewModal from './FinalReview.tsx';
import {
  CustomerLocation,
  CustomerLocationAddress,
  TradingPartner,
  TradingPartnerAddress,
} from '../../common/types/models.ts';
import { useNavigate } from 'react-router-dom';

export interface SendPedigreeData {
  tradingPartnerAddressId?: number;
  verifiedTradingPartnerEligibility?: VerifiedTradingPartnerAPI.Response.ReadOneIsAs2Active | null;
  selectedEmailAddresses: string[];
}

export interface AdditionalSendPedigreeData {
  poNumber: string;
  invoiceNumber: string;
  advanceShipNotice: string;
}

export interface sendPedigreeFinalReviewData {
  customerLocation?: CustomerLocation;
  customerLocationAddress?: CustomerLocationAddress;
  tradingPartner?: TradingPartner;
  tradingPartnerAddress?: TradingPartnerAddress;
  meds?: MedAPI.Response.ReadOneBySerialNumberPedigree[];
  additionalData?: AdditionalSendPedigreeData;
}

const SendPedigree = () => {
  const navigate = useNavigate();

  const [customerLocationAddressId, setCustomerLocationAddressId] =
    useState<number>();
  const [sendData, setSendData] = useState<SendPedigreeData>({
    selectedEmailAddresses: [],
  });
  const [outboundMeds, setOutboundMeds] = useState<
    MedAPI.Response.ReadOneBySerialNumberPedigree[]
  >([]);
  const [additionalData, setAdditionalData] =
    useState<AdditionalSendPedigreeData>();
  const [isOpenFinalReviewModal, setIsOpenFinalReviewModal] =
    useState<boolean>(false);
  const [finalReviewData, setFinalReviewData] =
    useState<sendPedigreeFinalReviewData>({});

  const { mutate: sendPedigrees } =
    mutationHooks.med.useCreateManySendPedigrees({
      onSuccess: data => {
        if (data) navigate('/sentepcisdocs');
      },
    });

  const send = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const { tradingPartnerAddressId, selectedEmailAddresses } = sendData;

    if (tradingPartnerAddressId && customerLocationAddressId)
      sendPedigrees({
        tradingPartnerAddressId,
        medIds: outboundMeds.map(({ med: { id } }) => id),
        recipientEmailAddresses: selectedEmailAddresses,
        customerLocationAddressId,
        ...additionalData,
      });
  };

  const isDisabled = () => {
    const {
      tradingPartnerAddressId,
      verifiedTradingPartnerEligibility,
      selectedEmailAddresses,
    } = sendData;

    if (
      !tradingPartnerAddressId ||
      !outboundMeds.length ||
      !customerLocationAddressId
    )
      return true;
    if (
      !verifiedTradingPartnerEligibility?.isAs2Active &&
      !selectedEmailAddresses.length
    )
      return true;

    return false;
  };

  return (
    <div>
      <div className="row">
        <CustomerLocationSelect
          setCustomerLocationAddressId={setCustomerLocationAddressId}
          setFinalReviewData={setFinalReviewData}
        />
      </div>
      <div className="row">
        <TradingPartnerSelect
          setSendData={setSendData}
          setFinalReviewData={setFinalReviewData}
        />
      </div>
      <div className="row">
        <MedList
          customerLocationId={finalReviewData.customerLocation?.id || 0}
          setOutboundMeds={setOutboundMeds}
          setFinalReviewData={setFinalReviewData}
        />
      </div>
      <div className="row">
        <AdditionalData
          setAdditionalData={setAdditionalData}
          setFinalReviewData={setFinalReviewData}
        />
      </div>
      <div className="row">
        <div className="col s12 m3">
          <a
            href="/"
            onClick={e => {
              e.preventDefault();
              setIsOpenFinalReviewModal(true);
            }}
            className={`btn-small blue white-text waves-effect waves-light col s12 ${
              isDisabled() && 'disabled'
            }`}
          >
            Send Pedigree{outboundMeds.length > 1 ? 's' : ''}
          </a>
        </div>
      </div>
      <FinalReviewModal
        isOpen={isOpenFinalReviewModal}
        setIsOpen={setIsOpenFinalReviewModal}
        data={finalReviewData}
        send={send}
      />
    </div>
  );
};

export default SendPedigree;
